import { useState } from "react";
import { LegalEntityAuditGroupRow, LegalEntityAuditGroupRowTableModel, LegalEntityAuditGroupViewModel } from "./LegalEntityAuditGroups.types";
import { BaseViewModel } from "../../system/systemOverview/SystemOverview.types";
import { DotLegalSelectOption } from "../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useTranslation } from "../../localization/useTranslation";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching";
import { AuditFieldGrouping, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage";
import { useClientFilteredPageView } from "@common/pageView/useClientFilteredPageView";
import { FilterPage } from "@common/pageView/PageView.types";
import {
    allAuditGroupColumns,
    AuditGroupColumn,
    auditGroupDefaultOrder,
    auditGroupDefaultOrderBy,
    toAuditGroupTableRows,
    useAuditGroupColumns,
} from "@common/components/questionnaireComponents/auditGroupTable/AuditGroupTable.columns";
import { exportDisplayedRows } from "@common/auditExport";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";

// A draft has never been sent, so it carries no year and drops out as soon as the sent filter is used.
const auditGroupFilterCandidates = [
    { id: "auditTemplates", candidate: (row: LegalEntityAuditGroupRow) => row.questionnaire.id.toString() },
    { id: "responsibles", candidate: (row: LegalEntityAuditGroupRow) => row.responsible.id.toString() },
    { id: "deadlines", candidate: (row: LegalEntityAuditGroupRow) => new Date(row.deadline).getTime().toString() },
    { id: "yearsSent", candidate: (row: LegalEntityAuditGroupRow) => (row.sent ? new Date(row.sent).getFullYear().toString() : null) },
    { id: "legalEntities", candidate: (row: LegalEntityAuditGroupRow) => row.legalEntityIds },
];

export function useLegalEntityAuditGroupsHooks() {
    const { translateDate, translateString } = useTranslation();
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [auditGroupToEdit, setAuditGroupToEdit] = useState<LegalEntityAuditGroupRowTableModel | undefined>(undefined);

    const pageView = useClientFilteredPageView<AuditGroupColumn>({
        page: FilterPage.LegalEntityAuditGroups,
        availableColumns: allAuditGroupColumns,
        defaultOrderBy: auditGroupDefaultOrderBy,
        defaultOrder: auditGroupDefaultOrder,
    });
    const { selectFilters, filterOperators } = pageView;
    const [searchString, setSearchString] = useStateSessionStorage<string>("audit-groups-overview-search", "");

    const url = `/legalEntityAuditGroups`;
    const { isLoading: auditGroupsAreLoading, data, refetch } = useDotLegalQuery<LegalEntityAuditGroupViewModel>(url);
    const isLoading = auditGroupsAreLoading || pageView.savedViewsAreLoading;

    let auditTemplateFilterOptions: Array<DotLegalSelectOption> = [];
    let deadlineFilterOptions: Array<DotLegalSelectOption> = [];
    let responsibleFilterOptions: Array<DotLegalSelectOption> = [];
    let rows: Array<LegalEntityAuditGroupRow> = [];

    if (data) {
        createDeadlineFilterOptions(data.rows);
        createAuditTemplateFilterOptions(data.rows.flatMap((x) => x.questionnaire));
        createResponsibleFilterOptions(data.rows.flatMap((x) => x.responsible));
        sortSelectOptions();
        rows = applyClientFilters(data.rows, auditGroupFilterCandidates, selectFilters, {}, filterOperators)!;
    }

    const tableRows: Array<LegalEntityAuditGroupRowTableModel> = toAuditGroupTableRows(rows, translateDate);

    const { columnLabels, tableFields, tableHeaders } = useAuditGroupColumns<LegalEntityAuditGroupRowTableModel>(
        "auditTemplate",
        "legalEntities",
        AuditFieldGrouping.ExternalAuditGroup,
        pageView.renderedColumns
    );

    async function onExportClick() {
        await exportDisplayedRows({
            path: "/legalEntityAuditGroups/Export",
            reportName: translateString("externalAudits"),
            pageView,
            columnLabels,
            rows: tableRows,
            searchString,
        });
    }

    const refetchAuditGroups = () => {
        refetch();
    };

    const getFilterDataOptions = () =>
        withOperators(
            [
                {
                    type: FilterOptionType.SelectField,
                    id: "auditTemplates",
                    value: selectFilters.auditTemplates ?? [],
                    label: translateString("auditTemplate"),
                    fixedData: auditTemplateFilterOptions,
                },
                {
                    type: FilterOptionType.SelectField,
                    id: "deadlines",
                    value: selectFilters.deadlines ?? [],
                    label: translateString("deadline"),
                    fixedData: deadlineFilterOptions,
                },
                {
                    type: FilterOptionType.SelectField,
                    id: "responsibles",
                    value: selectFilters.responsibles ?? [],
                    label: translateString("responsible"),
                    fixedData: responsibleFilterOptions,
                },
                {
                    type: FilterOptionType.SelectField,
                    id: "yearsSent",
                    value: selectFilters.yearsSent ?? [],
                    label: translateString("sent"),
                    fixedData: data?.sentYearFilterOptions ?? [],
                },
                {
                    type: FilterOptionType.SelectField,
                    id: "legalEntities",
                    value: selectFilters.legalEntities ?? [],
                    label: translateString("legalEntity"),
                    fixedData: data?.legalEntityFilterOptions ?? [],
                },
            ],
            filterOperators
        );

    return {
        pageView,
        tableFields,
        tableHeaders,
        onExportClick,
        showCreateDialog,
        setShowCreateDialog,
        rows: tableRows,
        isLoading,
        hasData: isLoading || (data && data.rows.length > 0),
        getFilterDataOptions,
        searchString,
        setSearchString,
        auditGroupToEdit,
        setAuditGroupToEdit,
        refetchAuditGroups,
    };

    function createDeadlineFilterOptions(rows: Array<LegalEntityAuditGroupRow>) {
        for (let row of rows) {
            const rowDeadline = new Date(row.deadline);
            if (!deadlineFilterOptions.some((x) => x.id === rowDeadline.getTime().toString())) {
                deadlineFilterOptions.push({
                    id: rowDeadline.getTime().toString(),
                    name: translateDate(rowDeadline),
                });
            }
        }
    }

    function createResponsibleFilterOptions(responsibles: Array<BaseViewModel>) {
        for (let responsible of responsibles) {
            if (!responsibleFilterOptions.some((x) => x.id === responsible.id)) {
                responsibleFilterOptions.push({
                    id: responsible.id,
                    name: responsible.name,
                });
            }
        }
    }

    function createAuditTemplateFilterOptions(auditTemplates: Array<BaseViewModel>) {
        for (let template of auditTemplates) {
            if (!auditTemplateFilterOptions.some((x) => x.id === template.id)) {
                auditTemplateFilterOptions.push({
                    id: template.id,
                    name: template.name,
                });
            }
        }
    }

    function sortSelectOptions() {
        auditTemplateFilterOptions.sort((a, b) => (a.name > b.name ? 1 : -1));
        deadlineFilterOptions.sort((a, b) => (a.id > b.id ? 1 : -1));
        responsibleFilterOptions.sort((a, b) => (a.name > b.name ? 1 : -1));
    }
}
